// Base design tokens - DESIGN.md compatible
// Shared across all themes, only colors change per theme

export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
};

export const radii = {
  none: 0,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  pill: 50,
  circle: '50%',
};

export const typography = {
  fontFamily: {
    primary: '"DM Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    mono: '"Fira Code", "Consolas", monospace',
  },
  fontSize: {
    xs: '0.65rem',
    sm: '0.75rem',
    md: '0.875rem',
    base: '1rem',
    lg: '1.25rem',
    xl: '1.5rem',
    xxl: '2rem',
    display: '2.5rem',
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    black: 900,
  },
  lineHeight: {
    tight: 1.1,
    normal: 1.5,
    relaxed: 1.75,
  },
  letterSpacing: {
    tight: '-0.02em',
    normal: '0',
    wide: '0.02em',
  },
};

export const shadows = {
  none: 'none',
  sm: (color = '#000') => `0 1px 4px ${color}14`,
  md: (color = '#000') => `0 2px 12px ${color}14`,
  lg: (color = '#000') => `0 4px 24px ${color}18`,
  xl: (color = '#000') => `0 8px 32px ${color}22`,
  glow: (color) => `0 0 20px ${color}40`,
  inner: (color = '#000') => `inset 0 2px 4px ${color}10`,
};

export const elevation = {
  flat: 0,
  raised: 1,
  floating: 2,
  modal: 3,
  toast: 4,
};

export const motion = {
  duration: {
    instant: '0ms',
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
    slower: '800ms',
  },
  easing: {
    linear: 'linear',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  },
};

export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 768,
  lg: 1024,
  xl: 1200,
};

export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  modal: 300,
  popover: 400,
  toast: 500,
  tooltip: 600,
};

// Semantic status colors - use with contrast utilities for auto text colors
export const statusColors = {
  bonus: '#ff6d00',
  goal: '#ffc107',
  shield: '#00bcd4',
  overdue: '#e65100',
  success: '#4caf50',
  error: '#f44336',
  warning: '#ff9800',
  info: '#2196f3',
  streak: '#ff6d00',
};

// Rarity colors for achievements/items (Finnish keys match backend)
export const rarityColors = {
  tavallinen: '#4caf50',    // Common - green
  harvinainen: '#2196f3',   // Rare - blue
  eeppinen: '#9c27b0',      // Epic - purple
  legendaarinen: '#ffc107', // Legendary - gold
};

// Component size tokens
export const componentSizes = {
  avatar: { xs: 32, sm: 40, md: 80, lg: 120 },
  iconBox: { sm: 32, md: 44, lg: 56 },
  chip: { xs: 17, sm: 20, md: 24, lg: 26 },
  progressBar: { xs: 6, sm: 8, md: 12, lg: 16 },
  button: { sm: 32, md: 40, lg: 48 },
};

// Surface alpha values for consistent transparency
export const surfaceAlpha = {
  subtle: 0.05,
  light: 0.08,
  medium: 0.12,
  elevated: 0.18,
  strong: 0.25,
};

// Dashboard palette defaults — generated from a theme's core colors per mode.
// Super themes can override any key in their light/dark objects.
export const buildModePalette = (theme, mode) => {
  const isDark = mode === 'dark';
  const accent = theme.main;
  const light = theme.light;
  const dark = theme.dark;
  const bg = isDark ? (theme.darkBg || '#1e1e2e') : (theme.bg || '#fff');

  return {
    // Column background
    bg,
    background: null,
    divider: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    borderColumn: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
    backgroundOverlay: isDark
      ? `linear-gradient(to bottom, ${bg} 0%, ${bg}66 60%, transparent 100%)`
      : `linear-gradient(to bottom, ${bg} 0%, ${bg}66 60%, transparent 100%)`,

    // Core colors
    accent,
    accentLight: light,
    accentDark: dark,
    contrastText: theme.contrastText || '#fff',

    // Avatar
    avatarBg: accent,
    avatarBorder: `3px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#fff'}`,
    avatarGlow: `0 4px 16px ${accent}44`,

    // Name / heading
    nameColor: accent,
    nameTextShadow: 'none',

    // Chips
    chipLevelBg: `${accent}22`,
    chipLevelColor: accent,
    chipStreakBg: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
    chipStreakActiveBg: statusColors.streak,
    chipStreakActiveColor: '#fff',
    chipShieldBg: statusColors.shield,
    chipShieldColor: '#fff',
    chipShieldDisabledBg: '#e0e0e0',
    chipShieldDisabledColor: '#999',

    // XP bar
    xpBarBg: `${accent}22`,
    xpBarFill: accent,
    xpBarBorder: 'none',
    xpBarBorderRadius: 50,

    // Coins pill
    coinsPillBg: accent,
    coinsPillText: '#fff',

    // Text
    textPrimary: isDark ? '#fff' : '#212121',
    textSecondary: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
    textMuted: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
    textDisabled: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.38)',

    // Weekly goal card
    weeklyGoalBg: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.85)',
    weeklyGoalBorder: 'none',
    weeklyGoalBorderRadius: '12px',

    // Progress bar
    progressTrackBg: isDark ? 'rgba(255,255,255,0.1)' : `${accent}22`,
    progressBonusZoneBg: isDark ? 'rgba(255,255,255,0.05)' : '#ffc10722',
    progressBonusBorder: '2px solid #ffc107',

    // Task rows
    cardBg: isDark ? 'rgba(255,255,255,0.12)' : '#fff',
    taskCompleteBg: accent,
    taskCompleteBackground: null,
    completedByOtherBg: isDark ? 'rgba(255,255,255,0.08)' : '#f0f0f0',
    overdueBg: isDark ? 'rgba(230, 81, 0, 0.25)' : 'rgba(230, 81, 0, 0.12)',
    taskBoxShadow: `0px 3px 0px 0px ${accent}`,
    taskBoxShadowCompleted: `0px 3px 0px 0px ${dark}`,
    taskBoxShadowOverdue: '0px 3px 0px 0px #e65100',
    taskBoxShadowHover: `0px 5px 0px 0px ${accent}`,
    taskBoxShadowActive: `0px 1px 0px 0px ${accent}`,
    taskBorder: 'none',
    taskBorderOverdue: '1.5px solid #ffcc02',
    taskBorderCompletedByOther: '1.5px solid #9e9e9e',
    taskBorderRadius: '20px',

    // Task text
    taskPendingText: null,
    taskCompleteText: '#fff',
    taskCompleteTextSecondary: 'rgba(255,255,255,0.75)',
    taskFontWeight: 700,
    taskOverdueText: statusColors.overdue,

    // Coin badges on tasks
    coinPendingBg: `${accent}50`,
    coinPendingText: '#fff',
    coinCompletedBg: accent,
    coinCompletedText: '#fff',
    coinDefaultBg: `${accent}18`,
    coinDefaultText: accent,
    coinGoalAchievedBg: 'rgba(255,193,7,0.2)',
    coinGoalAchievedText: '#ff8f00',

    // Status colors (theme-overridable)
    statusOverdue: statusColors.overdue,
    statusBonus: statusColors.bonus,
    statusShield: statusColors.shield,
    statusStreak: statusColors.streak,
    statusSuccess: statusColors.success,

    // Bonus button
    bonusBtnBorderColor: accent,
    bonusBtnColor: accent,
    bonusBtnBg: 'transparent',
    bonusBtnBoxShadow: `0px 3px 0px 0px ${accent}`,
    bonusBtnBoxShadowHover: `0px 5px 0px 0px ${accent}`,
    bonusBtnBoxShadowActive: `0px 1px 0px 0px ${accent}`,
    bonusBtnHoverBg: `${accent}11`,

    // Bonus task cards (same as normal task cards by default)
    bonusCardBg: isDark ? 'rgba(255,255,255,0.12)' : '#fff',
    bonusCardBorder: `1.5px solid ${accent}22`,
    bonusCardBoxShadow: `0px 3px 0px 0px ${accent}`,
    bonusCardBoxShadowHover: `0px 5px 0px 0px ${accent}`,
    bonusCardBoxShadowActive: `0px 1px 0px 0px ${accent}`,

    // Bonus panel
    bonusPanelBg: isDark ? (theme.darkBg || '#1e1e2e') : (theme.bg || '#fff'),
    bonusPanelBorder: `3px solid ${accent}`,
    bonusPanelBoxShadow: isDark ? '0 -4px 20px rgba(0,0,0,0.5)' : '0 -4px 20px rgba(0,0,0,0.12)',
    bonusPanelText: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',

    // Scheduled tasks
    scheduledDivider: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
    scheduledTaskBg: isDark ? 'rgba(255,255,255,0.12)' : '#fff',
    scheduledExpiredBg: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
  };
};
